import { useLang } from 'hooks/lang';

import {
  Button,
  CommandMenuEmpty,
  CommandMenuGroup,
  CommandMenuInput,
  CommandMenuItem,
  CommandMenuList,
  CommandMenuModal,
} from 'components/Blocks';
import { EntityStatusTag } from 'components/Entities';
import { SearchIcon } from 'components/Icons';

import { useMultiKeyPress, useOS, useToggleV2 } from 'hooks';
import { useProject } from 'providers/Project';
import { useRouting } from 'routes';

import { sortByName } from 'utils/array';

export function ProjectSearch() {
  const { t } = useLang();

  const [isOpen, { turnOff, turnOn }] = useToggleV2();

  const { projectList, loadingProjects, recentProjects, projects } = useProject();
  const routing = useRouting();

  const os = useOS();

  useMultiKeyPress({
    key: 'S',
    triggerKey: 'ctrl',
    action: turnOn,
    isEnabled: !loadingProjects,
  });

  const goToProject = (projectId: string) => {
    turnOff();
    routing.projectProfile({ projectId });
  };

  const actualRecent = recentProjects.filter((id) => !!projects[id]);

  if (loadingProjects) return null;

  return (
    <>
      <Button color="gray" variant="outlined" onClick={turnOn} className="-mr-2 border-none">
        <SearchIcon color="primary" />
        <span className="phone:hidden">{t('search-projects')}</span>
        <kbd className="pointer-events-none select-none items-center gap-1 rounded border px-1.5 font-mono text-xxs font-medium small_tablet:hidden">
          {os === 'macOS' ? '⌘' : <span className="text-[8px]">Ctrl+</span>}S
        </kbd>
      </Button>

      <CommandMenuModal isOpen={isOpen} close={turnOff} className="w-full" allowEscape>
        <CommandMenuInput placeholder={`${t('search-projects')}...`} />

        <CommandMenuList>
          <CommandMenuEmpty>{t('no-project-found')}</CommandMenuEmpty>

          {!!actualRecent.length && (
            <CommandMenuGroup heading={t('recently-visited')}>
              {actualRecent.map((id) => (
                <CommandMenuItem
                  key={id}
                  value={projects[id].name}
                  onSelect={() => goToProject(id)}
                >
                  <EntityStatusTag target="project" status={projects[id].status} round={false} />

                  {projects[id].name}
                </CommandMenuItem>
              ))}
            </CommandMenuGroup>
          )}

          <CommandMenuGroup heading={t('active-projects')}>
            {sortByName(projectList)
              .filter((p) => !recentProjects.includes(p.id))
              .map(({ name, status, id }) => (
                <CommandMenuItem key={id} value={name} onSelect={() => goToProject(id)}>
                  <EntityStatusTag target="project" status={status} round={false} />

                  {name}
                </CommandMenuItem>
              ))}
          </CommandMenuGroup>
        </CommandMenuList>
      </CommandMenuModal>
    </>
  );
}
